import React, { useContext, useEffect, useState } from 'react'
import { Drawer } from '@mui/material'
import { GlobalContext } from '../../store/context/globalContext'
import {
  getCurrency,
  getSymbolOfCurrency,
  formatNumber,
} from '../../../../utils/CurrencyFormatter'
import { InfoTooltip } from '../../../../utils/Utils'

type GrantCalculatorDrawerProps = {
  isOpen: boolean
  onClose: () => void
  grant: any
}

export function GrantCalculatorDrawer({ isOpen, onClose, grant }: GrantCalculatorDrawerProps) {
  const {
    companyName = '-',
    exercisePriceOrSarValue = 0,
    strikePriceinUserCurrency =0,
    numberGranted = 0,
    numberExercised = 0,
    vestedOptions = 0,
    vestingSchedules = [],
    // companyFMV = 0,
    companyFmvinUserCurrency =0,
  } = grant ?? {}

  const vestingFMV = vestingSchedules?.[0]?.currentFmv ?? 0
  const defaultFMV = companyFmvinUserCurrency || vestingFMV

  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)
  const currencySymbol = getSymbolOfCurrency(userCurrency)

  const [optionsToExercise, setOptionsToExercise] = useState<number>(vestedOptions)
  // const [strikePrice, setStrikePrice] = useState<number>(exercisePriceOrSarValue)
  const [strikePrice, setStrikePrice] = useState<number>(strikePriceinUserCurrency)
  const [currentFMV, setCurrentFMV] = useState<number>(defaultFMV)
  const [taxRate, setTaxRate] = useState<number>(20)
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false)

  useEffect(() => {
    if (grant) {
      setOptionsToExercise(vestedOptions - numberExercised)
      // setStrikePrice(exercisePriceOrSarValue)
      setStrikePrice(strikePriceinUserCurrency)

      setCurrentFMV(defaultFMV)
    }
  // }, [grant, vestedOptions, numberExercised, exercisePriceOrSarValue, defaultFMV])
  }, [grant, vestedOptions, numberExercised, strikePriceinUserCurrency, defaultFMV])


  if (!isOpen || !grant) return null

  const totalExerciseCost = optionsToExercise * strikePrice
  const paperGain = (currentFMV - strikePrice) * optionsToExercise
  const estimatedTax = paperGain > 0 ? (paperGain * taxRate) / 100 : 0
  const totalOutOfPocket = totalExerciseCost + estimatedTax
  const toExercise = vestedOptions - numberExercised

  const formatCurrency = (value: number): string => {
    return `${currencySymbol}${formatNumber({
      value: Number(value),
      format: userCurrencyFormat,
    })}`
  }

  const handleOptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    if (value >= 0 && value <= toExercise) {
      setOptionsToExercise(value)
    }
  }

  return (
    <Drawer anchor='right' open={isOpen} onClose={onClose}>
      <div className='p-6' style={{ width: '420px', maxWidth: '100vw' }}>
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-calculator fs-4 text-dark' />
            <h5 className='fw-bold mb-0'>Exercise Cost Calculator</h5>
          </div>
          <button className='btn btn-sm btn-icon' onClick={onClose}>
            <i className='bi bi-x-lg' />
          </button>
        </div>

        {/* Grant Details */}
        <div className='border rounded p-4 mb-4 bg-white'>
          <h6 className='fw-bold mb-4'>Grant Details</h6>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <div className='text-gray-700'>Company:</div>
            <div className='fw-semibold text-end'>{companyName}</div>
          </div>
          <div className='d-flex justify-content-between align-items-center'>
            <div className='text-gray-700'>Available to Exercise:</div>
            <div className='fw-semibold'>{toExercise?.toLocaleString(userCurrencyFormat)} options</div>
          </div>
        </div>

        {/* Exercise Parameters */}
        <h6 className='fw-semibold mb-4'>Exercise Parameters</h6>
        <div className='mb-4'>
          <label className='form-label'>Number of Options to Exercise</label>
          <input
            type='number'
            className='form-control'
            value={optionsToExercise}
            onChange={handleOptionsChange}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
            max={toExercise}
          />
          <div className='form-text text-gray-700'>
            Maximum: {toExercise?.toLocaleString(userCurrencyFormat)} options
          </div>
        </div>

        <div className='mb-4'>
          <label
            className='form-label d-flex align-items-center gap-1'
            title='Price you pay to exercise each option'
          >
            Strike Price per Option ({currencySymbol})
            <i className='bi bi-info-circle text-muted cursor-pointer' />
          </label>
          <input
            type='number'
            className='form-control'
            // value={strikePrice}
            value={strikePriceinUserCurrency}
            // onChange={(e) => setStrikePrice((parseFloat(Number(e.target.value).toFixed(2))))}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
            step={0.01}
          />
          <div className='form-text text-gray-700'>Pre-filled from grant data</div>
        </div>

        <div className='mb-4'>
          <label
            className='form-label d-flex align-items-center gap-1'
            title='Latest available FMV from the company or 409A valuation'
          >
            FMV per Option ({currencySymbol})
            <i className='bi bi-info-circle text-muted cursor-pointer' />
          </label>
          <input
            type='number'
            className='form-control'
            // value={currentFMV}
            value={companyFmvinUserCurrency}
            onChange={(e) => setCurrentFMV(Number(e.target.value))}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
            step={0.01}
          />
          <div className='form-text text-gray-700'>Current market value</div>
        </div>

        {/* Advanced Settings */}
        <div className='mb-4'>
          <button
            className='d-flex align-items-center justify-content-between w-100 border-0 bg-transparent text-dark fw-semibold'
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            title='Estimated Tax Rate (can be adjusted)'
          >
            <span className='d-flex align-items-center gap-2'>
              Advanced Settings
              <i className='bi bi-info-circle text-muted cursor-pointer' />
            </span>
            <i className={`bi bi-chevron-down ${isAdvancedOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {isAdvancedOpen && (
            <div className='mt-3'>
              <label className='form-label'>Estimated Tax Rate (%)</label>
              <input
                type='number'
                className='form-control'
                value={taxRate}
                onChange={(e) => setTaxRate(Number(e.target.value))}
                onWheel={(e) => e.currentTarget.blur()}
                min={0}
                max={100}
                step={0.1}
              />
              <div className='form-text'>Default: 20% (adjust based on your tax bracket)</div>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <h6 className='fw-semibold mb-3'>Cost Breakdown</h6>
        <div className='border rounded p-4 bg-light'>
          <div className='d-flex justify-content-between mb-2'>
            <div>Total Exercise Cost</div>
            <div className='fw-semibold'>{formatCurrency(totalExerciseCost)}</div>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <div>Paper Gain</div>
            <div className={`fw-semibold ${paperGain >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(paperGain)}
            </div>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <div>Estimated Tax Payable ({taxRate}%)</div>
            <div className='fw-semibold text-warning'>{formatCurrency(estimatedTax)}</div>
          </div>
          <hr />
          <div className='d-flex justify-content-between fw-bold'>
            <div>Total Out-of-Pocket Cost</div>
            <div>{formatCurrency(totalOutOfPocket)}</div>
          </div>
        </div>

        {/* Summary Text */}
        <div className='mt-4 p-3 bg-light rounded'>
          <p className='text-gray-600 fst-italic mb-0'>
            "To exercise {optionsToExercise?.toLocaleString(userCurrencyFormat)} options, you'll pay{' '}
            {formatCurrency(totalExerciseCost)} in strike price and {formatCurrency(estimatedTax)} in taxes
            — a total of {formatCurrency(totalOutOfPocket)}."
          </p>
        </div>

        {/* Footer Actions */}
        <div className='d-flex justify-content-end mt-4'>
          <button className='btn btn-outline-secondary me-2' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Drawer>
  )
}